import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { MCard } from "components/Shared";
import { getInspectorDailySummary } from "requests/inspectorTimeCard/index.ts";

const daysOfTheWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const totalColIndex = 7;

const Summary = ({ employeeName, inspectorNumber, payPeriodId, filterDateCallBack }) => {
  const mounted = useRef(true);
  const [inspectorSummary, setInspectorSummary] = useState();
  const [hoverClass, setHoverClass] = useState(Array(8).fill(""));
  const tableElem = useRef(null);

  useEffect(() => () => mounted.current = false, []);
  useEffect(() => {
    const curTable = tableElem.current;
    if (!curTable) return;

    const handleTableHover = (event) => {
      const dayCol = Number(event.target?.getAttribute("abbr"));
      if (Number.isNaN(dayCol)) return;
      const newCols = Array(8).fill("");
      newCols[dayCol] = "hover";
      setHoverClass(newCols);
    };
    const handleTableBlur = () => setHoverClass(Array(8).fill(""));
    const handleTableClick = (event) => {
      const dayCol = Number(event.target?.getAttribute("abbr"));
      if (Number.isNaN(dayCol) || dayCol >= totalColIndex || !filterDateCallBack) return;
      const day = daysOfTheWeek[dayCol];
      const filterDate = inspectorSummary?.[0]?.[day]?.date;
      if (filterDate) filterDateCallBack(filterDate);
    };

    curTable.addEventListener("mouseover", handleTableHover);
    curTable.addEventListener("mouseout", handleTableBlur);
    curTable.addEventListener("click", handleTableClick);

    return () => {
      curTable.removeEventListener("mouseover", handleTableHover);
      curTable.removeEventListener("mouseout", handleTableBlur);
      curTable.removeEventListener("click", handleTableClick);
    };
  }, [filterDateCallBack, inspectorSummary]);

  useEffect(() => {
    setInspectorSummary(null);
    getInspectorDailySummary(inspectorNumber, payPeriodId, mapAndSetInspectorSummary, mounted);
  }, [inspectorNumber, payPeriodId]);

  const mapAndSetInspectorSummary = data => {
    const reduceToDays = (obj, total, field = "hours") => obj.timeByDayList.reduce((acc, d) => {
      acc[daysOfTheWeek[d.dayOfWeek]] = { value: d[field] ?? 0, date: d.date };
      return acc;
    }, { total: total === undefined ? "" : total });

    setInspectorSummary([
      { type: "Production Hours", ...reduceToDays(data.productionDailyHours, data.productionHoursTotal) },
      { type: "Non Production Hours", ...reduceToDays(data.nonProductionDailyHours, data.nonProductionHoursTotal) },
      { type: "Total Hours", ...reduceToDays(data.dailyHoursTotal) },
      { type: "Calendar Hours", ...reduceToDays(data.calendarDailyHours, data.calendarHoursTotal) },
      { type: "Exterior Cases", ...reduceToDays(data.exteriorDailyCaseCount, data.exteriorCaseCountTotal, "count") },
      { type: "Interior Cases", ...reduceToDays(data.interiorDailyCaseCount, data.interiorCaseCountTotal, "count") },
      { type: "Specialty Cases", ...reduceToDays(data.specialtyDailyCaseCount, data.specialtyCaseCountTotal, "count") },
    ]);
  };

  return (
    <MCard title="Summary">
      <div className="qa-table-employee-timelogs">
        <table ref={tableElem}>
          <thead>
            <tr>
              <th className="qa-row" style={{ width: "160px" }}>{employeeName}</th>
              {daysOfTheWeek.map((day, idx) => <th key={day} className={`qa-col ${hoverClass[idx]}`} abbr={idx}>{day}</th>)}
              <th className={`qa-col ${hoverClass[totalColIndex]}`} abbr={totalColIndex}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(inspectorSummary || []).map((row) => (
              <tr key={row.type} className="border-top">
                <th className="qa-row">{row.type}</th>
                {daysOfTheWeek.map((day, idx) => <td key={`${row.type}-${day}`} className={hoverClass[idx]} abbr={idx}>{row[day]?.value ?? ""}</td>)}
                <td className={hoverClass[totalColIndex]} abbr={totalColIndex}>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MCard>
  );
};

Summary.propTypes = {
  employeeName: PropTypes.string,
  inspectorNumber: PropTypes.number.isRequired,
  payPeriodId: PropTypes.number.isRequired,
  filterDateCallBack: PropTypes.func,
};

export default Summary;
